/**
 * 顧客詳細「変更履歴」タブ (2026-07-28)。
 *
 * フォーム送信による顧客情報の自動更新 (上書き / 追加) の監査ログを表示する。
 * 書き込みは onFormSubmissionCreated のみ (rules で client write:false)。
 * 誤った上書きに気付いたときは、この履歴の「変更前」を見て基本情報タブから手で戻す。
 */
import type { ContactFieldChange } from '@goenhub/shared';
import { useQuery } from '@tanstack/react-query';
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
} from 'firebase/firestore';
import { ArrowRight, History } from 'lucide-react';
import { Link } from 'react-router-dom';

import { EmptyState } from '@/components/common/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { db } from '@/lib/firebase';
import { paths } from '@/routes/paths';

interface Props {
  contactId: string;
}

const MODE_LABEL: Record<ContactFieldChange['mode'], string> = {
  overwrite: '上書き',
  fill: '新規登録',
  append: '追加して既定に',
};

function fmtDateTime(v: unknown): string {
  if (!v) return '-';
  const d =
    typeof v === 'string'
      ? new Date(v)
      : typeof v === 'object' && v !== null && 'toDate' in v
        ? (v as { toDate: () => Date }).toDate()
        : null;
  if (!d || Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ContactChangesTab({ contactId }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['contact-field-changes', contactId],
    queryFn: async () => {
      const snap = await getDocs(
        query(
          collection(db, 'mail_contacts', contactId, 'field_changes'),
          orderBy('changedAt', 'desc'),
          limit(200),
        ),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as ContactFieldChange),
      }));
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <EmptyState
        icon={History}
        title="変更履歴はありません"
        description="フォーム送信によって顧客情報が更新されると、ここに記録されます。"
      />
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-[11px] text-[var(--text-muted)]">
        フォーム送信による自動更新の記録です（最新 200 件）。手動編集は記録されません。誤った更新は「変更前」を参考に基本情報タブから戻してください。
      </p>
      <div className="overflow-x-auto rounded border border-[var(--border)]">
        <table className="w-full text-[12px] min-w-[720px]">
          <thead className="bg-[var(--bg-elevated)] text-[var(--text-secondary)]">
            <tr>
              <th className="text-left font-semibold px-3 py-2 whitespace-nowrap">
                日時
              </th>
              <th className="text-left font-semibold px-3 py-2 whitespace-nowrap">
                項目
              </th>
              <th className="text-left font-semibold px-3 py-2 whitespace-nowrap">
                種別
              </th>
              <th className="text-left font-semibold px-3 py-2">変更内容</th>
              <th className="text-left font-semibold px-3 py-2 whitespace-nowrap">
                送信元フォーム
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((c) => (
              <tr
                key={c.id}
                className="border-t border-[var(--border)] align-top"
              >
                <td className="px-3 py-2 whitespace-nowrap text-[var(--text-secondary)]">
                  {fmtDateTime(c.changedAt)}
                </td>
                <td className="px-3 py-2 whitespace-nowrap font-semibold">
                  {c.fieldLabel || c.field}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <Badge
                    variant={c.mode === 'overwrite' ? 'draft' : 'default'}
                  >
                    {MODE_LABEL[c.mode] ?? c.mode}
                  </Badge>
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-start gap-2 flex-wrap">
                    <span className="text-[var(--text-muted)] line-through break-all">
                      {c.before || '（未登録）'}
                    </span>
                    <ArrowRight className="size-3 mt-1 flex-shrink-0 text-[var(--text-muted)]" />
                    <span className="break-all">{c.after}</span>
                  </div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {c.formId ? (
                    <Link
                      to={paths.formBuilder.replace(':id', c.formId)}
                      className="text-[var(--accent)] hover:underline"
                    >
                      {c.formName || c.formId}
                    </Link>
                  ) : (
                    '-'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
